/**
 * gradientRamp — SHARED gradient-ramp resolution for HATCH gradients.
 *
 * AutoCAD gradient hatches (LINEAR / CYLINDER / SPHERICAL / …) carry a colour
 * ramp in DXF group codes 450–470 (modelled on `HatchEntity.gradient`). BOTH
 * render pipes need the SAME ramp resolution:
 *
 *   - the PDF exporter (`print/pdf/converters/hatchConverter.ts`) projects it
 *     onto a REAL axial/radial `/Shading`;
 *   - the on-screen scene (`scene3d/scene/sceneMeshFactories.buildHatchFillMesh`)
 *     bakes it into a 256×1 `CanvasTexture` ramp.
 *
 * This module is the SINGLE source of truth for:
 *   - {@link gradientNameToShading} — the 9 predefined AutoCAD gradient names →
 *     axial/radial + INV* / symmetric / curved flags;
 *   - {@link resolveStopColor} — one gradient stop → RGB (trueColor → aci →
 *     fallback precedence);
 *   - {@link resolveGradientRamp} — the entity-agnostic CORE: oneColor tint
 *     lerp-toward-white, INV* endpoint swap, two-colour stops → {@link
 *     GradientRampSpec};
 *   - {@link CURVED_EASING_N} — the CURVED / INVCURVED easing exponent (shared
 *     with the PDF `/Function` `N`).
 *
 * # Dependency direction (CLAUDE.md ПРИОРИТЕТ #2 — dep-graph red zone)
 *
 * This file lives in `scene3d/hatch/` and is imported UP by `print/pdf` (exactly
 * like `getPatternDefinition` in `hatchPattern.ts`). The reverse edge —
 * `scene3d → print/pdf` — is FORBIDDEN. The PDF colour resolvers
 * (`hexToPlotRgb` / `aciToPlotRgb`) live in `print/pdf/pdfTransform` and apply a
 * PLOT tweak (white → black so geometry reads on the white page) that is WRONG
 * for the on-screen ramp (where white shows true). So this module does NOT
 * import any colour resolver: callers PASS a {@link StopColorResolvers} pair.
 * The PDF caller passes its plot-tweaked resolvers; the scene caller passes
 * non-tweaked ones. The module itself stays colour-policy-agnostic and pulls in
 * NOTHING from `print/pdf`.
 */

// The one `RgbColor` definition lives in the neutral `color/rgbCodec` leaf
// (shared with the PDF pipe — NOT a `scene3d → print/pdf` edge). Imported for
// local use here and re-exported so existing `import { RgbColor } from
// "…/gradientRamp"` callers keep their path.
import type { RgbColor } from "../../color/rgbCodec";

export type { RgbColor } from "../../color/rgbCodec";

/**
 * Colour resolvers a caller supplies so this module stays free of any colour
 * policy (and of the `print/pdf` dependency). Each maps a raw colour to an
 * {@link RgbColor}:
 *   - `hexToRgb("#rrggbb")` — a true-colour stop (DXF 421);
 *   - `aciToRgb(aci)`       — an ACI-indexed stop (DXF 63).
 * The PDF caller passes its white→black plot-tweaked variants; the scene caller
 * passes faithful (untweaked) ones.
 */
export interface StopColorResolvers {
  hexToRgb(hex: string): RgbColor;
  aciToRgb(aci: number): RgbColor;
}

/** Pure white — the single-colour tint endpoint (AutoCAD mixes toward white). */
const GRADIENT_WHITE: RgbColor = { r: 1, g: 1, b: 1 };

/**
 * CURVED / INVCURVED easing exponent.
 *
 * AutoCAD's CURVED gradient is an EASED ramp (the colour change accelerates
 * toward one end) rather than the LINEAR straight ramp. The PDF exporter feeds
 * this to FunctionType 2's exponential interpolation `C(t) = C0 + t^N·(C1−C0)`;
 * the scene ramp bakes `lerp(c0, c1, t^N)` into its texels. We use `N = 2` (a
 * quadratic ease-in) as a documented AutoCAD-APPEARANCE APPROXIMATION — AutoCAD
 * does not publish the exact curve, and ezdxf models the gradient by name only
 * (no curve coefficients), so an exact match is unobtainable from the reference
 * sources. N = 2 gives the recognisable "curved" non-linear ramp.
 */
export const CURVED_EASING_N = 2;

/** Resolved gradient name → shading family + flags. */
export interface GradientShadingMap {
  shadingType: "axial" | "radial";
  inverse: boolean;
  symmetric: boolean;
  curved: boolean;
}

/**
 * The entity-agnostic resolved gradient ramp. Carries the shading family +
 * flags, the geometry params (rotation/shift — in RADIANS for rotation, DXF
 * 460), the single-colour diagnostics, and the two resolved RGB endpoints
 * (INV* swap + tint fold already applied). Both render pipes derive their final
 * geometry (PDF axis/disc coords, scene UV + texture) from this.
 */
export interface GradientRampSpec {
  name: string;
  shadingType: "axial" | "radial";
  inverse: boolean;
  symmetric: boolean;
  curved: boolean;
  /** Gradient rotation in RADIANS (DXF group 460). */
  rotation: number;
  /** Gradient shift in `[0, 1]` (DXF group 461). 0 = centred. */
  shift: number;
  /** Single-colour tint mode (DXF group 452 = 1). Recorded for diagnostics. */
  oneColor: boolean;
  /** Tint value in `[0, 1]` (DXF group 462). Recorded for diagnostics. */
  tint: number;
  /** Resolved RGB for ramp end 0 (after INV swap + tint fold). */
  color0: RgbColor;
  /** Resolved RGB for ramp end 1 (after INV swap + tint fold). */
  color1: RgbColor;
}

/** The gradient block shape this module consumes (a structural subset of the
 *  model `HatchGradient`, so a full `HatchGradient` assigns without a copy). */
export interface GradientBlock {
  oneColor: number;
  rotation: number;
  shift: number;
  tint: number;
  name: string;
  colors: ReadonlyArray<{ position: number; aci?: number; trueColor?: number }>;
}

/**
 * Map a gradient NAME (DXF group 470) to its shading family + the INV* /
 * symmetric / curved flags. Covers all 9 predefined AutoCAD gradient names:
 *
 *   | name              | shadingType | inverse | symmetric | curved |
 *   |-------------------|-------------|---------|-----------|--------|
 *   | LINEAR            | axial       | no      | no        | no     |
 *   | CYLINDER          | axial       | no      | yes       | no     |
 *   | INVCYLINDER       | axial       | yes     | yes       | no     |
 *   | CURVED            | axial       | no      | no        | yes    |
 *   | INVCURVED         | axial       | yes     | no        | yes    |
 *   | SPHERICAL         | radial      | no      | no        | no     |
 *   | INVSPHERICAL      | radial      | yes     | no        | no     |
 *   | HEMISPHERICAL     | radial      | no      | no        | no     |
 *   | INVHEMISPHERICAL  | radial      | yes     | no        | no     |
 *
 * An unknown name defaults to LINEAR (axial, plain) so a future / mistyped
 * gradient name still produces a recognisable ramp rather than nothing.
 *
 * HEMISPHERICAL shares SPHERICAL's geometry (centred radial disc); AutoCAD
 * renders the highlight slightly off-centre, but the gradient model carries no
 * offset coefficient, so the centre offset is a documented APPROXIMATION (same
 * disc as SPHERICAL).
 */
export function gradientNameToShading(name: string): GradientShadingMap {
  switch (name.trim().toUpperCase()) {
    case "LINEAR":
      return { shadingType: "axial", inverse: false, symmetric: false, curved: false };
    case "CYLINDER":
      return { shadingType: "axial", inverse: false, symmetric: true, curved: false };
    case "INVCYLINDER":
      return { shadingType: "axial", inverse: true, symmetric: true, curved: false };
    case "CURVED":
      return { shadingType: "axial", inverse: false, symmetric: false, curved: true };
    case "INVCURVED":
      return { shadingType: "axial", inverse: true, symmetric: false, curved: true };
    case "SPHERICAL":
      return { shadingType: "radial", inverse: false, symmetric: false, curved: false };
    case "INVSPHERICAL":
      return { shadingType: "radial", inverse: true, symmetric: false, curved: false };
    case "HEMISPHERICAL":
      return { shadingType: "radial", inverse: false, symmetric: false, curved: false };
    case "INVHEMISPHERICAL":
      return { shadingType: "radial", inverse: true, symmetric: false, curved: false };
    default:
      return { shadingType: "axial", inverse: false, symmetric: false, curved: false };
  }
}

/**
 * Resolve ONE gradient stop to an RGB. Precedence (matches the model's stop
 * shape): `trueColor` (0xRRGGBB) → `aci` (via the caller's ACI resolver) → the
 * `fallback` (when a stop carries neither — defensive, keeps the ramp visible).
 * Colour POLICY (e.g. the PDF white→black plot tweak) lives entirely in the
 * caller-supplied {@link StopColorResolvers}.
 */
export function resolveStopColor(
  stop: { aci?: number; trueColor?: number } | undefined,
  fallback: RgbColor,
  resolvers: StopColorResolvers,
): RgbColor {
  if (!stop) return fallback;
  if (stop.trueColor !== undefined && Number.isFinite(stop.trueColor)) {
    const c = stop.trueColor >>> 0;
    const hex = `#${((c & 0xffffff) >>> 0).toString(16).padStart(6, "0")}`;
    return resolvers.hexToRgb(hex);
  }
  if (stop.aci !== undefined && Number.isFinite(stop.aci)) {
    return resolvers.aciToRgb(stop.aci);
  }
  return fallback;
}

/**
 * Resolve a gradient block's ramp + flags into a {@link GradientRampSpec} — the
 * entity-agnostic CORE shared by the PDF `resolveGradientSpec` wrapper and the
 * scene `resolveSceneGradient`.
 *
 * Two-colour (`oneColor === 0`): color0 = stops[0], color1 = stops[last].
 * Single-colour (`oneColor === 1`): color0 = the base stop colour; color1 is the
 * TINT endpoint. AutoCAD's single-colour gradient mixes the base colour toward
 * WHITE by the tint slider: a tint of 1 leaves the colour unchanged, a tint of 0
 * fades it fully to white. We model this as
 *   `color1 = lerp(color0, white, 1 − tint)`
 * i.e. `color1 = color0·tint + white·(1 − tint)`.
 *
 * KNOWN APPROXIMATION: AutoCAD's exact single-colour tint blend is not published
 * and ezdxf models the tint as a scalar only (no blend formula), so the
 * lerp-toward-white above is a documented AutoCAD-APPEARANCE approximation (it
 * matches the observed "slider toward white" behaviour).
 *
 * INV* names swap the two endpoints (c0↔c1) — applied here so downstream always
 * reads color0 → color1 in ramp order.
 */
export function resolveGradientRamp(
  g: GradientBlock,
  fallback: RgbColor,
  resolvers: StopColorResolvers,
): GradientRampSpec {
  const map = gradientNameToShading(g.name);

  // Stops sorted by position so stops[0] is the 0.0 end and the last is 1.0.
  const stops = [...(g.colors ?? [])].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0),
  );

  const base = resolveStopColor(stops[0], fallback, resolvers);
  let c0: RgbColor;
  let c1: RgbColor;
  if (g.oneColor === 1) {
    // Single colour + tint toward white (documented approximation above).
    const t = Math.max(0, Math.min(1, g.tint));
    c0 = base;
    c1 = {
      r: base.r * t + GRADIENT_WHITE.r * (1 - t),
      g: base.g * t + GRADIENT_WHITE.g * (1 - t),
      b: base.b * t + GRADIENT_WHITE.b * (1 - t),
    };
  } else {
    // Two-colour: stops[0] → stops[last]. A degenerate single-stop gradient
    // reuses the base colour for both ends (a flat ramp, still valid).
    c0 = base;
    c1 = resolveStopColor(stops[stops.length - 1] ?? stops[0], fallback, resolvers);
  }

  // INV* → swap the colour order so the ramp reads color0 → color1.
  if (map.inverse) {
    const tmp = c0;
    c0 = c1;
    c1 = tmp;
  }

  return {
    name: g.name,
    shadingType: map.shadingType,
    inverse: map.inverse,
    symmetric: map.symmetric,
    curved: map.curved,
    rotation: g.rotation,
    shift: g.shift,
    oneColor: g.oneColor === 1,
    tint: g.tint,
    color0: c0,
    color1: c1,
  };
}
